import { useCallback, useMemo, useState } from 'react';
import dayJs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { Box, Button, IconButton, InputAdornment, Paper, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { appConfig } from '@app/app';
import { useAuth } from '@app/auth';
import { Paginated } from '@app/query';
import { error } from '@app/snackbars';
import { Table, useTableState } from '@app/ui/table';
import { Tooltip } from '@app/ui/tooltip';
import { Label } from '@app/ui/label';
import { Text, DebouncedText } from '@app/ui/forms';
import { INTERNAL_USER_ROLES, USER_ROLE_COLOR, UserRoleEnum } from '@app/user/constants';

import { USER_GET_MANY_WITH_PAGINATION, USER_UPDATE_ROLE } from '../gql';

interface UserRow {
  id: number;
  name: string;
  role: UserRoleEnum;
  roleAssignedAt?: string | null;
}

// SAA-79 D6/D12: this screen filters/assigns among internal roles only — `TableFilter<T>` is
// keyed on `status`, so a local Filter fits this screen's `role` filter instead.
interface Filter {
  search?: string;
  role?: UserRoleEnum | 'all';
}

export const AdminSettingsList = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();

  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [pendingRole, setPendingRole] = useState<UserRoleEnum | undefined>(undefined);

  // D7 — name ascending default, unlike every other table's `createdAt desc`.
  const [tableState, onChangeTableState] = useTableState<Filter>({
    orderBy: [{ direction: 'asc', field: 'name' }],
    role: 'all',
  });

  // 'all' is a UI sentinel stripped client-side before the query, same idiom as AdminUserList's
  // status filter (`:74-78`) — the server arg stays undefined.
  const preparedTableState = useMemo(() => {
    const { role, ...rest } = tableState;

    return role === 'all' ? rest : tableState;
  }, [tableState]);

  const { data, loading, refetch } = useQuery<{ userGetManyWithPagination?: Paginated<UserRow> }>(
    USER_GET_MANY_WITH_PAGINATION,
    { variables: preparedTableState },
  );

  const [userUpdateRole, { loading: saveLoading }] = useMutation(USER_UPDATE_ROLE);

  const roleOptions = useMemo(
    () => INTERNAL_USER_ROLES.map(role => ({ id: role, name: t(`user.field.role.option.${role}`) })),
    [t],
  );

  const roleFilterOptions = useMemo(
    () => [{ id: 'all', name: t('user.page.adminSettings.roleFilterAll') }, ...roleOptions],
    [roleOptions],
  );

  const handleCancel = useCallback(() => {
    setEditingRowId(null);
    setPendingRole(undefined);
  }, []);

  // Reached ONLY from the edit action button — deliberately NOT from an onRowClick. The design's
  // prototype does put onClick on the whole row, but a click anywhere in a row silently mutating
  // state is easy to trigger by accident, so edit is an explicit action here.
  const handleEnterEdit = useCallback(
    (id: number, role: UserRoleEnum) => {
      // §4.3(a) — own row is a no-op; the real gate is the server-side self-change block.
      if (id === user?.id) {
        return;
      }

      setEditingRowId(id);
      setPendingRole(role);
    },
    [user?.id],
  );

  const handleSave = useCallback(async () => {
    if (editingRowId === null || !pendingRole) {
      return;
    }

    try {
      await userUpdateRole({ variables: { input: { id: editingRowId, role: pendingRole } } });
      await refetch();
      handleCancel();
    } catch (e) {
      error(t('general.error.somethingWentWrong'));
    }
  }, [editingRowId, pendingRole, userUpdateRole, refetch, handleCancel, t]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: `${t('user.page.adminSettings.roleColumn')}`,
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'role',
        headerName: `${t('user.page.adminSettings.rolesColumn')}`,
        flex: 1,
        minWidth: 170,
        sortable: false,
        renderCell: (params: GridRenderCellParams) =>
          editingRowId === params.row.id ? (
            <Text
              select
              label={t('user.field.role.assignLabel')}
              options={roleOptions}
              value={pendingRole}
              onChange={e => setPendingRole(e.target.value as UserRoleEnum)}
            />
          ) : (
            <Label
              text={t(`user.field.role.option.${params.value}`)}
              // `lineHeight: 'normal'` is REQUIRED, not cosmetic: MUI DataGrid sets the cell's
              // line-height to rowHeight-1 to centre text, and Label is a plain Box that inherits it —
              // giving a 65px badge inside a 60px row, clipped, with the text off-centre. Same reason
              // AdminUserList.tsx:173 does it. Design wants 26px, which 'normal' produces.
              sx={{ bgcolor: USER_ROLE_COLOR[params.value], color: '#FFF', lineHeight: 'normal' }}
            />
          ),
      },
      {
        field: 'roleAssignedAt',
        headerName: `${t('user.page.adminSettings.assignedDateColumn')}`,
        flex: 1,
        minWidth: 150,
        maxWidth: 200,
        // Blank while the row is being edited, per the design (its edit-mode row renders an empty
        // <span> in this slot) — the stored date is about to be replaced, so showing it misleads.
        // renderCell rather than valueFormatter so there is one source of truth for the cell.
        renderCell: (params: GridRenderCellParams) =>
          editingRowId === params.row.id ? null : (
            <>{params.value ? dayJs(params.value).format(appConfig.format.date) : '-'}</>
          ),
      },
      {
        field: 'actions',
        headerName: `${t('user.field.actions.label')}`,
        type: 'actions',
        // Save (72) + Cancel (85) + MUI's 8px actions gap = 165, plus 20px padding each side = 205.
        // `headerAlign` does NOT default to `align` on an actions column — set both, or the label
        // stays centred while the buttons move left.
        width: 205,
        minWidth: 205,
        align: 'left',
        headerAlign: 'left',
        getActions: (params: GridRowParams) => {
          if (editingRowId === params.row.id) {
            return [
              <Button
                key="save"
                variant="contained"
                color="primary"
                disabled={saveLoading}
                onClick={handleSave}
                sx={{ height: 36 }}
              >
                {t('user.page.adminSettings.saveButton')}
              </Button>,
              <Button key="cancel" variant="contained" color="primaryLight" onClick={handleCancel} sx={{ height: 36 }}>
                {t('user.page.adminSettings.cancelButton')}
              </Button>,
            ];
          }

          // §4.3(a) — cosmetic FE omission of the edit control on the actor's own row; the real
          // gate is the server-side self-change block in UserService.updateRole.
          if (params.row.id === user?.id) {
            return [];
          }

          return [
            <Tooltip key="edit" title={t('user.page.adminSettings.editTooltip')} placement="top">
              <IconButton
                className="filledTonal"
                color="secondary"
                sx={{ p: 0.5, width: 33, height: 33, borderRadius: '50%' }}
                onClick={() => handleEnterEdit(params.row.id, params.row.role)}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>,
          ];
        },
      },
    ],
    [editingRowId, pendingRole, roleOptions, user?.id, handleEnterEdit, handleSave, handleCancel, saveLoading, t],
  );

  return (
    // SAA-150 F1 — the page-level "Admin Settings" title moved to `SettingsScreen` (shared across
    // all three tabs); this card's top-left corner stays square so it sits flush under the tab bar,
    // matching the `AdminApplicationItem.tsx` tab/panel precedent.
    <Paper variant="outlined" elevation={0} sx={{ borderRadius: '0 16px 16px 16px', p: { xs: 2, sm: 3, lg: 4 } }}>
      {/* Design: title and subtitle are one 8px-gap stack, 16px above the filters. `subtitle1` is
            the theme's 16px/400/1.4 — `body1` is 14px, which read as a caption next to the 24px title. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Typography variant="h2">{t('user.page.adminSettings.cardTitle')}</Typography>
        {/* Design subtitle is #333 (text.primary), not the grey used elsewhere. */}
        <Typography variant="subtitle1" color="text.primary">
          {t('user.page.adminSettings.cardSubtitle')}
        </Typography>
      </Box>

      {/* Fixed 360/180 widths with a 16px gap, per the design. A responsive Grid can only split the
            card proportionally (md={4}/md={3} gave 362/266), so it cannot express two unequal fixed
            widths — hence a plain flex row that collapses to full-width on xs. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ width: { xs: '100%', sm: 360 } }}>
          <DebouncedText
            label={t('application.field.search.label')}
            placeholder={`${t('user.page.adminSettings.searchPlaceholder')}`}
            value={tableState.search}
            onChange={search => onChangeTableState({ search })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="medium" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 180 } }}>
          <Text
            select
            label={t('user.field.role.label')}
            options={roleFilterOptions}
            value={tableState.role}
            onChange={e => onChangeTableState({ role: e.target.value as UserRoleEnum | 'all' })}
          />
        </Box>
      </Box>

      <Table
        // Design rows are min-height 107px — and edit mode needs it: the "Assign role" select with
        // its label is ~75px, which the shared 60px default clips. (Overridable as of SAA-79.)
        rowHeight={107}
        loading={loading}
        columns={columns}
        rows={data?.userGetManyWithPagination?.rows || []}
        rowCount={data?.userGetManyWithPagination?.pagination?.totalItems || 0}
        state={tableState}
        onChangeState={onChangeTableState}
        getRowClassName={params => (editingRowId === params.row.id ? 'admin-settings-row-editing' : '')}
        sx={{
          '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.200' },
          // Both classes on the same element (0,3,0) — a lone `.admin-settings-row-editing` ties
          // with DataGrid's own row rule and loses on source order, computing to a near-invisible
          // rgba(0,0,0,0.016). The `&:hover` twin is needed or hover repaints over it.
          '& .MuiDataGrid-row.admin-settings-row-editing, & .MuiDataGrid-row.admin-settings-row-editing:hover': {
            bgcolor: 'grey.200',
          },
          // The theme's `:last-of-type` rule gives the last CELL 36px of left padding but not its
          // HEADER (a `MuiDataGrid-filler` sibling makes the header not last-of-type), so without
          // this the buttons sit 16px right of the "Actions" label. 20px = every other column.
          '& .MuiDataGrid-cell[data-field="actions"]': { px: '20px' },
          // A renderCell cell is `display: block` with `align-items: normal`, so in edit mode the
          // 68px label+select group sat 6px from the top with 33px below it — 14px above centre.
          // The design's roles cell is `display:flex; align-items:center`. Applies to both modes.
          '& .MuiDataGrid-cell[data-field="role"]': { display: 'flex', alignItems: 'center' },
        }}
      />
    </Paper>
  );
};
